/**
 * Editor das caixas do modo overlay: mostra a página do PDF do Canva
 * (renderizada com pdf.js) e, por cima, as caixas onde os valores da
 * proposta serão desenhados. Arrastar numa área vazia cria uma caixa; arrastar
 * uma caixa move; o canto inferior direito redimensiona.
 *
 * Coordenadas guardadas em pontos PDF (origem no topo da página); a tela só
 * aplica a escala.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export type OverlayField = {
  key: string;
  text?: string;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  fontSize: number;
  align: "left" | "center" | "right";
  color: string;
  bold?: boolean;
  cover?: boolean;
  coverColor?: string;
};

type Drag =
  | { kind: "create"; page: number; startX: number; startY: number; curX: number; curY: number }
  | { kind: "move"; index: number; offX: number; offY: number }
  | { kind: "resize"; index: number };

const VIEW_W = 620;

export default function OverlayFieldEditor({
  pdfUrl,
  pages,
  fields,
  onChange,
  keyOptions,
}: {
  pdfUrl: string;
  pages: Array<{ width: number; height: number }>;
  fields: OverlayField[];
  onChange: (fields: OverlayField[]) => void;
  keyOptions: Array<{ key: string; label: string; group: string }>;
}) {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [rendering, setRendering] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Documento carregado E a URL de onde veio: trocar a URL (reexportar) tem
  // que trocar o documento, senão a arte antiga fica na tela com as medidas novas.
  const docRef = useRef<{ url: string; doc: pdfjs.PDFDocumentProxy } | null>(null);
  const renderTaskRef = useRef<pdfjs.RenderTask | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);

  const size = pages[page - 1] ?? { width: 595, height: 842 };
  const scale = VIEW_W / size.width;
  const viewH = Math.round(size.height * scale);

  // Renderiza a página corrente. Um render por vez no canvas: o anterior é
  // cancelado antes (virar página rápido lançava "same canvas during multiple
  // render() operations" e deixava a página em branco).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRendering(true);
      try {
        renderTaskRef.current?.cancel();
        renderTaskRef.current = null;
        if (!docRef.current || docRef.current.url !== pdfUrl) {
          docRef.current?.doc.cleanup().catch(() => undefined);
          docRef.current = { url: pdfUrl, doc: await pdfjs.getDocument({ url: pdfUrl, withCredentials: true }).promise };
        }
        if (cancelled) return;
        const p = await docRef.current.doc.getPage(page);
        const viewport = p.getViewport({ scale: scale * (window.devicePixelRatio || 1) });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${VIEW_W}px`;
        canvas.style.height = `${viewH}px`;
        const task = p.render({ canvasContext: canvas.getContext("2d")!, viewport, canvas } as any);
        renderTaskRef.current = task;
        await task.promise;
      } catch (err) {
        // Cancelamento é esperado ao virar página; o resto vira aviso na tela.
        if (!(err instanceof Error && err.name === "RenderingCancelledException")) {
          toast.error("Não consegui desenhar a página do PDF.");
        }
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pdfUrl, page, scale, viewH]);
  useEffect(() => () => { renderTaskRef.current?.cancel(); docRef.current?.doc.cleanup().catch(() => undefined); }, []);

  const toPdf = (px: number) => px / scale;
  const toPx = (pt: number) => pt * scale;
  const pointer = (e: React.PointerEvent) => {
    const r = layerRef.current!.getBoundingClientRect();
    return { x: Math.max(0, Math.min(VIEW_W, e.clientX - r.left)), y: Math.max(0, Math.min(viewH, e.clientY - r.top)) };
  };
  const update = (i: number, patch: Partial<OverlayField>) => onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  const onLayerDown = (e: React.PointerEvent) => {
    if (e.target !== layerRef.current) return;
    const { x, y } = pointer(e);
    setSelected(null);
    setDrag({ kind: "create", page, startX: x, startY: y, curX: x, curY: y });
    layerRef.current!.setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const { x, y } = pointer(e);
    if (drag.kind === "create") setDrag({ ...drag, curX: x, curY: y });
    else if (drag.kind === "move") {
      const f = fields[drag.index];
      update(drag.index, { x: Math.max(0, Math.min(size.width - f.w, toPdf(x - drag.offX))), y: Math.max(0, Math.min(size.height - f.h, toPdf(y - drag.offY))) });
    } else if (drag.kind === "resize") {
      const f = fields[drag.index];
      update(drag.index, { w: Math.max(12, toPdf(x) - f.x), h: Math.max(8, toPdf(y) - f.y) });
    }
  };
  const onUp = () => {
    if (drag?.kind === "create") {
      const x0 = Math.min(drag.startX, drag.curX), y0 = Math.min(drag.startY, drag.curY);
      const w = Math.abs(drag.curX - drag.startX), h = Math.abs(drag.curY - drag.startY);
      if (w > 8 && h > 8) {
        const next: OverlayField = { key: keyOptions[0]?.key ?? "campo", page, x: toPdf(x0), y: toPdf(y0), w: toPdf(w), h: toPdf(h), fontSize: 11, align: "left", color: "#111827", cover: true, coverColor: "#ffffff" };
        onChange([...fields, next]);
        setSelected(fields.length);
      }
    }
    setDrag(null);
  };

  const sel = selected !== null ? fields[selected] : null;
  const groups = useMemo(() => {
    const m = new Map<string, typeof keyOptions>();
    for (const o of keyOptions) m.set(o.group, [...(m.get(o.group) ?? []), o]);
    return [...m.entries()];
  }, [keyOptions]);
  const labelOf = (k: string) => keyOptions.find((o) => o.key === k)?.label ?? k;

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <Button type="button" size="icon" variant="outline" className="h-7 w-7" disabled={page <= 1} onClick={() => { setPage(page - 1); setSelected(null); }}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-xs tabular-nums">Página {page} / {pages.length}</span>
            <Button type="button" size="icon" variant="outline" className="h-7 w-7" disabled={page >= pages.length} onClick={() => { setPage(page + 1); setSelected(null); }}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <span className="text-[11px] text-muted-foreground">Arraste numa área vazia para criar uma caixa.</span>
        </div>
        <div className="relative border rounded-md bg-white overflow-hidden select-none" style={{ width: VIEW_W, height: viewH }}>
          <canvas ref={canvasRef} className="absolute inset-0" />
          {rendering && <div className="absolute inset-0 flex items-center justify-center bg-white/60"><Loader2 className="h-5 w-5 animate-spin" /></div>}
          <div ref={layerRef} className="absolute inset-0 cursor-crosshair" onPointerDown={onLayerDown} onPointerMove={onMove} onPointerUp={onUp}>
            {fields.map((f, i) => f.page === page && (
              <div
                key={i}
                className={`absolute border text-[10px] leading-tight overflow-hidden ${selected === i ? "border-primary bg-primary/15 z-10" : "border-sky-500 bg-sky-500/10"}`}
                style={{ left: toPx(f.x), top: toPx(f.y), width: toPx(f.w), height: toPx(f.h), cursor: "move" }}
                onPointerDown={(e) => { e.stopPropagation(); const p = pointer(e); setSelected(i); setDrag({ kind: "move", index: i, offX: p.x - toPx(f.x), offY: p.y - toPx(f.y) }); layerRef.current!.setPointerCapture(e.pointerId); }}
                title={labelOf(f.key)}
              >
                <span className="px-1 font-mono text-primary">{f.text?.trim() ? f.text.slice(0, 60) : `{{${f.key}}}`}</span>
                <div className="absolute right-0 bottom-0 w-3 h-3 bg-primary cursor-se-resize" onPointerDown={(e) => { e.stopPropagation(); setSelected(i); setDrag({ kind: "resize", index: i }); layerRef.current!.setPointerCapture(e.pointerId); }} />
              </div>
            ))}
            {drag?.kind === "create" && (
              <div className="absolute border border-dashed border-primary bg-primary/10" style={{ left: Math.min(drag.startX, drag.curX), top: Math.min(drag.startY, drag.curY), width: Math.abs(drag.curX - drag.startX), height: Math.abs(drag.curY - drag.startY) }} />
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3" data-overlay-props>
        <div className="space-y-1">
        <div className="text-xs font-medium">Caixas ({fields.length})</div>
        <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
          {fields.length === 0 && <div className="p-2 text-xs text-muted-foreground">Nenhuma ainda. Desenhe sobre a página onde entra cada dado.</div>}
          {fields.map((f, i) => (
            <button key={i} type="button" className={`w-full text-left px-2 py-1 text-xs hover:bg-accent ${selected === i ? "bg-accent" : ""}`} onClick={() => { setSelected(i); if (f.page !== page) setPage(f.page); }}>
              <span className="font-mono">{f.text?.trim() ? "texto livre" : f.key}</span> <span className="text-muted-foreground">· p.{f.page}</span>
            </button>
          ))}
        </div>
        </div>
        {sel && selected !== null ? (
          <div className="rounded-md border p-2 space-y-2" data-overlay-selected>
            <div>
              <Label className="text-[10px]">O que entra aqui</Label>
              <Select value={sel.text !== undefined ? "__text__" : sel.key} onValueChange={(v) => v === "__text__" ? update(selected, { text: sel.text ?? `{{${sel.key}}}` }) : update(selected, { key: v, text: undefined })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__text__">Texto livre com {"{{chaves}}"}</SelectItem>
                  {groups.map(([g, list]) => (
                    <div key={g}>
                      <div className="px-2 py-1 text-[10px] uppercase text-muted-foreground">{g}</div>
                      {list.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {sel.text !== undefined && (
              <div>
                <Label className="text-[10px]">Texto (as chaves são trocadas ao gerar)</Label>
                <Textarea rows={3} className="text-xs font-mono" value={sel.text} onChange={(e) => update(selected, { text: e.target.value })} placeholder={"Para a {{cliente_organizacao}}"} />
                <p className="text-[10px] text-muted-foreground mt-0.5">Cubra a linha ou o parágrafo inteiro e reescreva-o aqui com as chaves no lugar dos dados.</p>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div><Label className="text-[10px]">Fonte (pt)</Label><Input type="number" min={4} max={200} className="h-8 text-xs" value={sel.fontSize} onChange={(e) => update(selected, { fontSize: Number(e.target.value) || 11 })} /></div>
              <div><Label className="text-[10px]">Alinhamento</Label>
                <Select value={sel.align} onValueChange={(v) => update(selected, { align: v as OverlayField["align"] })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="left">Esquerda</SelectItem><SelectItem value="center">Centro</SelectItem><SelectItem value="right">Direita</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px]">Cor do texto</Label><Input type="color" className="h-8 p-1" value={sel.color} onChange={(e) => update(selected, { color: e.target.value })} /></div>
              <div><Label className="text-[10px]">Cor da cobertura</Label><Input type="color" className="h-8 p-1" value={sel.coverColor ?? "#ffffff"} onChange={(e) => update(selected, { coverColor: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-xs"><Checkbox checked={!!sel.bold} onCheckedChange={(c) => update(selected, { bold: c === true })} /> Negrito</label>
            <label className="flex items-center gap-2 text-xs"><Checkbox checked={!!sel.cover} onCheckedChange={(c) => update(selected, { cover: c === true })} /> Cobrir o que está por baixo</label>
            <div className="text-[10px] text-muted-foreground">x {Math.round(sel.x)} · y {Math.round(sel.y)} · {Math.round(sel.w)}×{Math.round(sel.h)} pt</div>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => { onChange(fields.filter((_, i) => i !== selected)); setSelected(null); }}>
              <Trash2 className="h-3 w-3 mr-1" /> Remover caixa
            </Button>
          </div>
        ) : (
          <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">Selecione uma caixa para ajustar o que entra nela, fonte, alinhamento e cobertura.</div>
        )}
      </div>
    </div>
  );
}
